import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Loader from '../../components/Common/Loader';
import SegToggle from '../../components/Common/SegToggle';
import Modal from '../../components/Common/Modal';
import CourseConstructor from '../../components/Admin/CourseConstructor';
import AdminVariantsPage from './AdminVariantsPage';
import CourseSettings from '../../components/Admin/CourseSettings';
import { getCourses } from '../../services/adminService';
import { bebasDigits } from '../../utils/bebasDigits';

// Минималистичные иконки разделов (показываются на вкладке, у модулей и в окне редактора)
const svg = (inner, sw = 1.8) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{inner}</svg>
);
export const SECTION_ICON = {
  sections: svg(<><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" /></>),
  homework: svg(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>),
  variants: svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" /></>),
  settings: svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>),
};

const TABS = [
  { key: 'sections', label: 'Вебинары' },
  { key: 'homework', label: 'Задания' },
  { key: 'variants', label: 'Варианты' },
];

const PLACEHOLDER = { sections: 'Поиск вебинара…', homework: 'Поиск ДЗ…', variants: 'Поиск варианта…' };

export default function CourseInnerPage() {
  const { id } = useParams();
  const courseId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  // Вкладку можно задать при переходе (например, с «Главной» из карточки ближайших уроков)
  const [tab, setTab] = useState(() => {
    const t = location.state?.tab;
    return TABS.some(x => x.key === t) ? t : 'sections';
  });
  const [search, setSearch] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Урок, на который нужно сразу открыть редактор (переход с «Главной»).
  // Сбрасываем после первого применения, иначе он «прилипнет» при смене вкладок.
  const [focusLessonId, setFocusLessonId] = useState(() => location.state?.lessonId ?? null);

  const loadCourse = () => getCourses()
    .then(res => setCourse((res.data || []).find(c => c.id === courseId) || null))
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => { loadCourse(); }, [courseId]); // eslint-disable-line

  const changeTab = (k) => { setTab(k); setSearch(''); setFocusLessonId(null); };
  const clearFocus = () => setFocusLessonId(null);

  if (loading) return <Loader />;

  const tabOptions = TABS.map(t => ({
    key: t.key,
    label: <span className="cc-tab-label">{SECTION_ICON[t.key]}{t.label}</span>,
  }));

  return (
    <div>
      {/* Шапка: кнопка «К курсам», под ней — название курса и подпись.
          marginTop -2px — верх кнопки на уровне верха бокового сайдбара (page-content pt 24 vs sidebar-brand pt 22). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18, marginTop: -2 }}>
        <button onClick={() => navigate('/admin/dashboard')} title="К курсам"
          style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: '9px 14px', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          К курсам
        </button>
        <div>
          <h1 className="s-page-title" style={{ margin: 0 }}>{bebasDigits(course?.name || 'Курс')}</h1>
          <p className="s-page-sub" style={{ marginTop: 4 }}>Содержимое курса</p>
        </div>
      </div>

      {/* Одна строка: слева — поиск по разделу, справа — вкладки и шестерёнка настроек */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="cc-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={PLACEHOLDER[tab]} />
        </div>
        <SegToggle className="glass" value={tab} onChange={changeTab} options={tabOptions} />
        <button className="cc-gear-btn" onClick={() => setSettingsOpen(true)} title="Настройки курса" aria-label="Настройки курса">
          {SECTION_ICON.settings}
        </button>
      </div>

      <div key={tab} className="cc-tab-fade">
        {tab === 'sections' && <CourseConstructor courseId={courseId} mode="sections" search={search} sectionIcon={SECTION_ICON.sections} course={course} onCourseChanged={loadCourse} focusLessonId={focusLessonId} onFocusDone={clearFocus} />}
        {tab === 'homework' && <CourseConstructor courseId={courseId} mode="homework" search={search} sectionIcon={SECTION_ICON.homework} course={course} onCourseChanged={loadCourse} focusLessonId={focusLessonId} onFocusDone={clearFocus} />}
        {tab === 'variants' && <AdminVariantsPage courseId={courseId} search={search} icon={SECTION_ICON.variants} course={course} onCourseChanged={loadCourse} focusVariantId={focusLessonId} onFocusDone={clearFocus} />}
      </div>

      <Modal bare isOpen={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <div style={{ width: 'min(640px, 100%)', margin: 'auto' }}>
          <CourseSettings course={course} onSaved={loadCourse} onClose={() => setSettingsOpen(false)} />
        </div>
      </Modal>
    </div>
  );
}
